import { Component, Input, ViewChild, inject } from "@angular/core";
import { ControlPermissionModel } from "@app/controls/models/ControlPermissionsModel";
import { ControlTestScoreChartConfig } from "./ControlTestScoreChartConfig";
import { ControlChartDataService } from "./control-chart.data.service";
import { SimpleTestingType } from "../../testing/simple/enums/SimpleTestingType";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { ControlAdvEffChartComponent } from "./control-adv-eff-chart/control-adv-eff-chart.component";
import { ControlTestscoreChartComponent } from "./control-testscore-chart/control-testscore-chart.component";
import { firstValueFrom } from "rxjs";
import { provideTranslationScope, TranslatePipe } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/controls/i18n/scope";

@Component({
    selector: "control-test-result",
    templateUrl: "./control-test-result.component.html",
    styleUrls: ["./control-test-result.component.scss"],
    imports: [TranslatePipe, ControlTestscoreChartComponent, ControlAdvEffChartComponent],
    viewProviders: [provideTranslationScope(scope)]
})
export class ControlTestResultComponent {
    private ds = inject(ControlChartDataService);

    @Input() cerrixTab: TabModel;
    @Input() guid: string;
    @Input() permissions: ControlPermissionModel;

    @ViewChild("diChart") diChart: ControlTestscoreChartComponent;
    @ViewChild("effSimple") effSimple: ControlTestscoreChartComponent;
    @ViewChild("effAdv") effAdv: ControlAdvEffChartComponent;

    isLoaded = false;

    async load(): Promise<void> {
        if (!this.guid || this.guid.length === 0) {
            return;
        }
        const createDiPromise = this.createDiChart();
        const createEffPromise = this.createEffChart();

        if (this.isLoaded) {
            return;
        }
        this.isLoaded = true;

        await createDiPromise;
        await createEffPromise;

        window.addEventListener("resize", () => {
            if (this.diChart && this.diChart.loadedChart) {
                this.diChart.loadedChart.reflow();
            }
            if (this.effSimple && this.effSimple.loadedChart) {
                this.effSimple.loadedChart.reflow();
            }
            if (this.effAdv && this.effAdv.chart && this.effAdv.chart.loadedChart) {
                this.effAdv.chart.loadedChart.reflow();
            }
        });
    }

    async createDiChart(): Promise<void> {
        if (!this.permissions.CanOpenDi) {
            return;
        }

        const data = await firstValueFrom(this.ds
            .getSimpleChart(SimpleTestingType.DesignImplementation, this.guid));
        const config = <ControlTestScoreChartConfig>{
            chartTitle: "",
            xTitle: "Test result date",
            xCategories: data.xAxis,
            yTitle: "Score",
            yCategories: data.yAxis.map((x) => x.Name),
            data: data.data,
        };

        this.diChart.createChart(config);
    }

    async createEffChart(): Promise<void> {
        if (this.permissions.CanOpenSimpleEff) {
            const data = await firstValueFrom(this.ds
                .getSimpleChart(SimpleTestingType.Effectiveness, this.guid));
            const config = <ControlTestScoreChartConfig>{
                chartTitle: "",
                xTitle: "Test result date",
                xCategories: data.xAxis,
                yTitle: "Score",
                yCategories: data.yAxis.map((x) => x.Name),
                data: data.data,
            };

            this.effSimple.createChart(config);
        } else if (this.permissions.CanOpenAdvEff) {
            this.effAdv.setup(this.guid);
        }
    }
}
